import { useState, useEffect, useRef } from 'react'
import api from '../api/client'
import Modal from './Modal'
import Boton from './Boton'
import { colors, text, space, radius, sx } from '../design/tokens'

/**
 * Buscador avanzado de clientes (la lupa / F6). Lo usan todas las pantallas
 * que eligen un cliente: si se mejora acá, mejora en todas.
 *
 * - Busca por PALABRAS en cualquier orden y campo (nombre, documento, código,
 *   email, teléfono, domicilio, localidad) — la regla vive en el backend.
 * - Las fichas con número de Presea van primero (`orden=codigo`): entre fichas
 *   duplicadas, la que factura es la que tiene código; la que no, se marca.
 * - Email y teléfono a la vista para distinguir duplicados.
 * - Teclado: ↑↓ elige, Enter selecciona, Esc cierra.
 */
export default function ModalListaClientes({ onSelect, onClose }) {
  const [q, setQ] = useState('')
  const [busqueda, setBusqueda] = useState('')   // q con debounce (lo que viaja)
  const [clientes, setClientes] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [sel, setSel] = useState(0)
  const listaRef = useRef(null)

  // Debounce: un pedido cuando se deja de tipear, no uno por tecla.
  useEffect(() => {
    const t = setTimeout(() => { setBusqueda(q.trim()); setPage(1) }, 250)
    return () => clearTimeout(t)
  }, [q])

  useEffect(() => {
    let vigente = true
    setLoading(true)
    const params = { page, activos: true, per_page: 50, orden: 'codigo' }
    if (busqueda) params.q = busqueda
    api.get('/clientes', { params })
      .then(({ data }) => {
        if (!vigente) return
        setClientes(data.data || [])
        setLastPage(data.last_page || 1)
        setTotal(data.total || 0)
        setSel(0)
      })
      .catch(() => {})
      .finally(() => { if (vigente) setLoading(false) })
    return () => { vigente = false }
  }, [busqueda, page])

  // El resaltado sigue al teclado también con scroll.
  useEffect(() => {
    listaRef.current?.querySelector(`[data-fila="${sel}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [sel])

  const alTecla = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, clientes.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)) }
    else if (e.key === 'Enter' && clientes[sel]) { e.preventDefault(); onSelect(clientes[sel]) }
  }

  const contacto = (c) => [c.email, c.telefono].filter(Boolean).join(' · ')

  return (
    <Modal title="Buscar cliente (F6)" onClose={onClose} maxWidth="980px">
      <div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}>
        <input
          autoFocus
          style={{ ...sx.input, width: '100%', boxSizing: 'border-box', padding: `${space.md}px ${space.lg}px` }}
          placeholder="Nombre, DNI/CUIT, código, email, teléfono o localidad — las palabras en cualquier orden"
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={alTecla}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: text.sec, color: colors.textSec }}>
          <span>{loading ? 'Buscando…' : `${total.toLocaleString('es-AR')} ${total === 1 ? 'cliente' : 'clientes'}`}</span>
          <span style={{ color: colors.textMeta }}>↑↓ elegir · Enter seleccionar · Esc cerrar</span>
        </div>

        {/* Altura FIJA (no maxHeight): el modal no cambia de tamaño mientras se
            tipea la búsqueda — la lista scrollea adentro (regla Martin 14 ago). */}
        <div ref={listaRef} style={{ height: 440, overflowY: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.md }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 104 }} /><col /><col style={{ width: 120 }} /><col style={{ width: 190 }} /><col style={{ width: 84 }} />
            </colgroup>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr style={{ background: colors.bgSoft }}>
                <th style={th}>Código</th>
                <th style={th}>Cliente · contacto</th>
                <th style={th}>Documento</th>
                <th style={th}>Localidad</th>
                <th style={th}>Lista</th>
              </tr>
            </thead>
            <tbody>
              {!loading && clientes.length === 0 && (
                <tr><td colSpan={5} style={{ padding: space.xl, textAlign: 'center', color: colors.textMeta }}>
                  {busqueda ? `Ningún cliente coincide con "${busqueda}".` : 'Sin clientes.'}
                </td></tr>
              )}
              {clientes.map((c, i) => (
                <tr
                  key={c.id}
                  data-fila={i}
                  onClick={() => onSelect(c)}
                  onMouseEnter={() => setSel(i)}
                  style={{ height: 48, borderBottom: `1px solid ${colors.borderSoft}`, cursor: 'pointer', background: i === sel ? colors.linkBg : undefined }}
                >
                  <td style={td}>
                    {c.codigo_presea
                      ? <span style={{ ...sx.mono, fontWeight: 600 }}>{c.codigo_presea}</span>
                      : <span style={{ ...sx.badge, background: colors.warnBg, color: colors.warn, whiteSpace: 'nowrap' }} title="Ficha sin número de Presea (todavía no facturó, o es un duplicado)">sin código</span>}
                  </td>
                  <td style={{ ...td, overflow: 'hidden' }}>
                    <div style={{ fontWeight: 500, color: colors.text, lineHeight: 1.3 }}>{c.nombre}</div>
                    {contacto(c) && <div style={sub} title={contacto(c)}>{contacto(c)}</div>}
                  </td>
                  <td style={{ ...td, ...sx.mono, fontSize: text.sec }}>{c.cuit || <span style={{ color: colors.textMeta }}>—</span>}</td>
                  <td style={{ ...td, overflow: 'hidden' }}>
                    <div style={{ lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.localidad || (c.provincia ? '' : '—')}</div>
                    {c.provincia && <div style={sub}>{c.provincia}</div>}
                  </td>
                  <td style={{ ...td, fontSize: text.sec, color: colors.textSec }}>{c.lista_precios || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* La fila del paginador SIEMPRE ocupa su lugar (visibility, no unmount):
            aparecer y desaparecer también hacía saltar la altura del modal. */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: space.lg, fontSize: text.body, visibility: lastPage > 1 ? 'visible' : 'hidden' }}>
          <Boton variante="ghost" sm disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Anterior</Boton>
          <span style={{ color: colors.textSec }}>Página {page} de {lastPage}</span>
          <Boton variante="ghost" sm disabled={page >= lastPage} onClick={() => setPage(p => p + 1)}>Siguiente →</Boton>
        </div>
      </div>
    </Modal>
  )
}

const th = { ...sx.th, padding: `${space.md}px ${space.md}px` }
const td = { padding: `${space.xs}px ${space.md}px`, verticalAlign: 'middle', color: colors.text }
const sub = { fontSize: text.sec, color: colors.textMeta, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }
